import React from 'react';
import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet, View } from './rn';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import HeaderBackButton from './HeaderBackButton';

type Props = {
  children: ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
  /**
   * Başlığı içeriğin ÜSTÜNDE yüzdürür (7.14 · D-550).
   *
   * İstenerek açılıyor: akıştaki başlık bugün on iki ekranda kullanılıyor ve
   * hepsini birden çevirmek, her birinin kaydırma boşluğunu aynı anda
   * değiştirmek demek. Yüzen başlık kullanan ekran, aynı yüksekliği
   * `screenHeaderContentInset` ile tepede geri vermek zorunda — yoksa ilk
   * satır başlığın altında kalır.
   */
  floating?: boolean;
};

// Bütün üst-seviye mobil sayfalarda geri kontrolünün optik başlangıç noktası:
// ekranın solundan 16 px, güvenli alanın altından 8 px. İçerik yüksekliği 56 px
// olduğundan 40x40 geri kontrolü tam olarak aynı koordinata oturur.
export default function ScreenHeader({ children, contentStyle, floating }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  /**
   * Yüzen başlığın altında kaydırma kenarı efekti var, cam bant yok (7.14 · D-704, D-776).
   *
   * Apple (Scroll views): denetimlerin altına özel arka plan konmaz; içerik
   * yüzen bir çubuğun altından geçiyorsa kaydırma kenarı efekti çubuğu
   * içerikten ayırır. Başlık yazısı cam denetimlerin dışında durduğu için
   * Apple'ın otomatik seçimi daha opak olan biçimdir. Bu yüzden bant sayfa
   * renginde opak, alt kenarı kısa bir geçişle içeriğe karışır. Denetimler
   * kendi camlarında durur. D-550'nin uyarısı sürüyor: yazının altı sürekli bir
   * yüzey olmalı, yoksa altından geçen satır yazının üstüne biner.
   */
  const yuzey = floating ? <ScrollEdgeEffect color={colors.white} /> : null;

  return (
    <View
      testID="screen-header-safe"
      pointerEvents={floating ? 'box-none' : undefined}
      style={[{ paddingTop: insets.top }, floating && styles.floating]}>
      {yuzey}
      <View testID="screen-header-content" style={[styles.content, contentStyle]}>{children}</View>
    </View>
  );
}

// Geçiş başlık satırının içinde kalır: 56 noktalık satırın son 12 noktası.
// Böylece sayfa tepede `screenHeaderContentInset` kadar boşluk bıraktığında ilk
// satır hiç solmaz; yalnız başlığın altına kayan içerik yumuşakça kaybolur.
const EDGE_FADE = 12;
const EDGE_FADE_STEPS = 6;

function ScrollEdgeEffect({ color }: { color: string }) {
  return (
    <View testID="screen-header-edge" style={styles.surface} pointerEvents="none">
      <View style={[styles.edgeSolid, { backgroundColor: color }]} />
      <View style={styles.edgeFade}>
        {Array.from({ length: EDGE_FADE_STEPS }, (_, i) => (
          <View
            key={i}
            testID="screen-header-edge-step"
            style={{ flex: 1, backgroundColor: color, opacity: 1 - (i + 1) / (EDGE_FADE_STEPS + 1) }}
          />
        ))}
      </View>
    </View>
  );
}

export function ScreenBackButton({ onPress }: { onPress: () => void }) {
  return (
    <View style={styles.overlay} pointerEvents="box-none">
      <ScreenHeader>
        <HeaderBackButton onPress={onPress} />
      </ScreenHeader>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    height: 56,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Bandın tamamını kaplar: güvenli alan dahil, içerik satırının altına kadar.
  surface: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  edgeSolid: { position: 'absolute', top: 0, left: 0, right: 0, bottom: EDGE_FADE },
  edgeFade: { position: 'absolute', left: 0, right: 0, bottom: 0, height: EDGE_FADE },
  // Yüzen başlık: içerik altından geçer, dokunma yalnız kontrollere iner.
  floating: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 72,
    zIndex: 1000,
  },
});
